import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Search, ShieldCheck, Users } from 'lucide-react'
import { accessApi, type AdminUser, type InviteResult, type UserInput, type UserKind, type UsersQuery } from '@/api/accessApi'
import { ListState, tdClass, thClass } from '@/components/admin/AdminShared'
import { TextAction } from '@/components/admin/catalog/CatalogShared'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'
import { NativeSelect } from '@/components/common/Field'
import { EmptyState, errorMessage } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useAccessMutation, useAdminUsers } from '@/hooks/useAccess'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { cn } from '@/lib/utils'
import { useAuth } from '@/providers/AuthProvider'
import { useToast } from '@/providers/ToastProvider'
import { UserDialog } from './UserDialog'

function statusOf(u: AdminUser): 'active' | 'invited' | 'disabled' {
  return !u.enabled ? 'disabled' : u.invitePending ? 'invited' : 'active'
}

const STATUS_TONE = {
  active: 'bg-status-resolved-bg text-status-resolved-fg',
  invited: 'bg-status-progress-bg text-status-progress-fg',
  disabled: 'bg-status-closed-bg text-status-closed-fg',
} as const

/** Users: find, invite, edit, disable (users.manage). Nothing is deleted; ticket history refers to people. */
export function UsersTab() {
  const { t } = useTranslation()
  const { user: me } = useAuth()
  const { notify } = useToast()
  const [text, setText] = useState('')
  const [kind, setKind] = useState<UserKind | ''>('')
  const [enabled, setEnabled] = useState<UsersQuery['enabled'] | ''>('')
  const q = useDebouncedValue(text.trim(), 250)
  const users = useAdminUsers({ q: q || undefined, kind: kind || undefined, enabled: enabled || undefined })

  const [editing, setEditing] = useState<AdminUser | null>(null)
  const [creating, setCreating] = useState(false)
  const [disabling, setDisabling] = useState<AdminUser | null>(null)
  const create = useAccessMutation(accessApi.createUser)
  const update = useAccessMutation(accessApi.updateUser)
  const toggle = useAccessMutation(accessApi.setUserEnabled)
  const resend = useAccessMutation(accessApi.resendInvite)

  const announce = (invite: InviteResult, email: string) =>
    notify({ title: t(`access.users.invite.${invite}`, { email }), tone: invite === 'not_configured' ? 'warning' : 'info' })
  const closeForm = () => {
    setCreating(false)
    setEditing(null)
    create.reset()
    update.reset()
  }
  const submit = (input: UserInput) => {
    if (editing) update.mutate({ ...input, id: editing.id }, { onSuccess: closeForm })
    else
      create.mutate(input, {
        onSuccess: (u) => {
          announce(u.invite, u.email)
          closeForm()
        },
      })
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2.5">
        <div className="relative w-full sm:w-72">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-placeholder" aria-hidden />
          <Input aria-label={t('access.users.search')} placeholder={t('access.users.search')} className="pl-9" value={text} onChange={(e) => setText(e.target.value)} />
        </div>
        <NativeSelect aria-label={t('access.users.cols.kind')} className="w-44" value={kind} onChange={(e) => setKind(e.target.value as UserKind | '')}>
          <option value="">{t('access.users.allKinds')}</option>
          <option value="AGENT">{t('access.kindsPlural.AGENT')}</option>
          <option value="CUSTOMER">{t('access.kindsPlural.CUSTOMER')}</option>
        </NativeSelect>
        <NativeSelect aria-label={t('access.users.cols.status')} className="w-40" value={enabled} onChange={(e) => setEnabled(e.target.value as UsersQuery['enabled'] | '')}>
          <option value="">{t('access.users.allStatuses')}</option>
          <option value="true">{t('access.users.active')}</option>
          <option value="false">{t('access.users.disabled')}</option>
        </NativeSelect>
        <div className="grow" />
        <Button onClick={() => setCreating(true)}>{t('access.users.new')}</Button>
      </div>

      <div className="overflow-x-auto rounded-lg border border-line">
        <ListState query={users}>
          {(users.data ?? []).length === 0 ? (
            <EmptyState compact icon={Users} message={t('access.users.empty')} />
          ) : (
            <table className="w-full min-w-[760px] table-fixed border-collapse" aria-label={t('access.tabs.users')}>
              <thead className="bg-surface-subtle">
                <tr>
                  <th className={thClass}>{t('access.users.cols.name')}</th>
                  <th className={`${thClass} w-32`}>{t('access.users.cols.kind')}</th>
                  <th className={`${thClass} w-44`}>{t('access.users.cols.role')}</th>
                  <th className={`${thClass} w-48`}>{t('access.users.cols.where')}</th>
                  <th className={`${thClass} w-28`}>{t('access.users.cols.status')}</th>
                  <th className={`${thClass} w-56`}>
                    <span className="sr-only">{t('access.users.edit')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {(users.data ?? []).map((u) => {
                  const status = statusOf(u)
                  const self = u.id === me?.id
                  // Only a superadmin can change a superadmin (the API refuses the rest).
                  const locked = u.isSuperAdmin && !me?.isSuperAdmin
                  return (
                    <tr key={u.id} className="border-b border-line last:border-b-0 hover:bg-surface-subtle">
                      <td className={tdClass}>
                        <p className="flex items-center gap-1.5 truncate font-medium text-ink-strong">
                          {u.firstName} {u.lastName}
                          {u.isSuperAdmin ? <ShieldCheck className="size-3.5 shrink-0 text-primary" aria-label={t('access.users.superAdmin')} /> : null}
                          {self ? <span className="rounded-sm bg-surface-subtle px-1.5 text-label font-normal text-ink-muted">{t('access.users.you')}</span> : null}
                        </p>
                        <p className="truncate text-label text-ink-muted">{u.email}</p>
                      </td>
                      <td className={tdClass}>{t(`access.kinds.${u.kind}`)}</td>
                      <td className={cn(tdClass, 'truncate')}>{u.role?.displayName ?? '—'}</td>
                      <td className={cn(tdClass, 'truncate')}>
                        {u.kind === 'AGENT' ? (u.team?.name ?? '—') : [u.customer?.name, u.tenant?.name].filter(Boolean).join(' · ')}
                      </td>
                      <td className={tdClass}>
                        <span className={cn('inline-block whitespace-nowrap rounded-sm px-2 py-[3px] text-label font-medium', STATUS_TONE[status])}>
                          {t(`access.users.status.${status}`)}
                        </span>
                      </td>
                      <td className={cn(tdClass, 'text-right')}>
                        <span className="inline-flex flex-wrap justify-end gap-3">
                          {status === 'invited' ? (
                            <TextAction
                              disabled={resend.isPending}
                              onClick={() => resend.mutate(u.id, { onSuccess: (r) => announce(r.invite, u.email), onError: (e) => notify({ title: errorMessage(e), tone: 'error' }) })}
                            >
                              {t('access.users.resend')}
                            </TextAction>
                          ) : null}
                          <TextAction disabled={locked} onClick={() => setEditing(u)}>
                            {t('access.users.edit')}
                          </TextAction>
                          {u.enabled ? (
                            <TextAction danger disabled={locked || self} onClick={() => setDisabling(u)}>
                              {t('access.users.disable')}
                            </TextAction>
                          ) : (
                            <TextAction
                              disabled={locked || toggle.isPending}
                              onClick={() => toggle.mutate({ id: u.id, enabled: true }, { onError: (e) => notify({ title: errorMessage(e), tone: 'error' }) })}
                            >
                              {t('access.users.enable')}
                            </TextAction>
                          )}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </ListState>
      </div>

      <UserDialog
        open={creating || editing !== null}
        user={editing}
        pending={create.isPending || update.isPending}
        error={create.error ?? update.error}
        onOpenChange={(o) => (o ? undefined : closeForm())}
        onSubmit={submit}
      />
      <ConfirmDialog
        open={disabling !== null}
        title={t('access.users.disableTitle', { name: disabling ? `${disabling.firstName} ${disabling.lastName}` : '' })}
        body={t('access.users.disableBody')}
        confirmLabel={t('access.users.disable')}
        destructive
        pending={toggle.isPending}
        error={toggle.error ? errorMessage(toggle.error) : null}
        onConfirm={() => disabling && toggle.mutate({ id: disabling.id, enabled: false }, { onSuccess: () => setDisabling(null) })}
        onOpenChange={(o) => {
          if (!o) {
            setDisabling(null)
            toggle.reset()
          }
        }}
      />
    </div>
  )
}
